// Hook that acquires a silent API access token for the current signed-in
// account. Falls back to popup if silent acquisition fails (e.g., new
// consent required). Returns `{ token, loading, error }`.

import { useMsal } from '@azure/msal-react';
import { InteractionRequiredAuthError } from '@azure/msal-browser';
import { useEffect, useState } from 'react';
import { apiTokenRequest } from './msal.js';

export interface TokenState {
  token: string | null;
  loading: boolean;
  error: Error | null;
}

export function useAccessToken(): TokenState {
  const { instance, accounts } = useMsal();
  const [state, setState] = useState<TokenState>({ token: null, loading: true, error: null });

  useEffect(() => {
    const account = accounts[0];
    if (!account) {
      setState({ token: null, loading: false, error: null });
      return;
    }

    let cancelled = false;
    void (async () => {
      setState({ token: null, loading: true, error: null });
      try {
        const res = await instance.acquireTokenSilent({ ...apiTokenRequest, account });
        if (!cancelled) setState({ token: res.accessToken, loading: false, error: null });
      } catch (err) {
        if (err instanceof InteractionRequiredAuthError) {
          try {
            const res = await instance.acquireTokenPopup({ ...apiTokenRequest, account });
            if (!cancelled) setState({ token: res.accessToken, loading: false, error: null });
          } catch (popupErr) {
            if (!cancelled) {
              setState({
                token: null,
                loading: false,
                error: popupErr instanceof Error ? popupErr : new Error('token popup failed'),
              });
            }
          }
        } else if (!cancelled) {
          setState({
            token: null,
            loading: false,
            error: err instanceof Error ? err : new Error('token acquisition failed'),
          });
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [instance, accounts]);

  return state;
}

/**
 * Narrow a possibly-null access token to a non-null string. Call this
 * inside React Query `queryFn`s that are gated by `enabled: !!token` —
 * the gate prevents the call at runtime, but TypeScript can't see that
 * invariant, so this helper provides the narrowing without a bare `!`.
 */
export function requireToken(token: string | null): string {
  if (!token) {
    throw new Error('access token is not available');
  }
  return token;
}
